import { IconSearch } from '@maia/icons';
import { Input, Pagination, Select, Table, ToggleButtonGroup } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useEffect } from 'react';
import { useFilter } from '../../../utils/filter/filter';
import { Insurances, getInsurancesOptions } from './queryOptions';
import { useInsuranceTable } from './tableOptions';

interface InsuranceTableProps {
  insurances: Insurances;
  productFilterOptions: Array<string>;
}

export function InsuranceTable(props: InsuranceTableProps) {
  const { insurances, productFilterOptions } = props;
  const { state, dispatch: setSearch, mappedState } = useFilter('/insurances');

  const { data } = useQuery(getInsurancesOptions(mappedState, insurances));

  const table = useInsuranceTable(data);

  const { sorting } = table.getState();

  useEffect(() => {
    setSearch({ type: 'setSort', payload: sorting });
  }, [setSearch, sorting]);

  const debouncedQuery = debounce((payload: string) => {
    setSearch({ type: 'setQuery', payload });
  }, 500);

  return (
    <Table
      table={table}
      className="grid-cols-[min-content_auto_auto_auto_min-content_min-content] "
      filterSlot={
        <div className="flex gap-x-24">
          <Input
            icon={<IconSearch />}
            placeholder="Zoeken"
            className="w-full max-w-[550px] mr-auto"
            onChange={(event) => debouncedQuery(event.target.value)}
            defaultValue={state.search}
          />
          <Select
            placeholder="Filter op product"
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'product')?.[1]
            }
            items={[
              { label: 'Alle', value: '', meta: {} },
              ...productFilterOptions.map((option) => ({
                label: option,
                value: option,
                meta: {},
              })),
            ]}
            onChange={(value) => {
              setSearch({
                type: 'setFilter',
                payload: ['product', value.toString()],
              });
            }}
          />
          <ToggleButtonGroup
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'status')?.[1]
            }
            items={[
              { label: 'Alles', value: '' },
              { label: 'Actief', value: 'active' },
              { label: 'In review', value: 'needs_review' },
              { label: 'Verlopen', value: 'cancelled' },
            ]}
            onChange={(status) => {
              setSearch({ type: 'setFilter', payload: ['status', status] });
            }}
          />
        </div>
      }
      paginationSlot={
        data?.pagination ? (
          <Pagination
            {...data.pagination}
            onLimitChange={(limit) => {
              setSearch({ type: 'setLimit', payload: limit });
            }}
            onPageChange={(page) => {
              setSearch({ type: 'paginate', payload: page });
            }}
          />
        ) : null
      }
    />
  );
}
